'use client';

import React, { useMemo, useImperativeHandle, useCallback, useRef } from 'react';
import { User2 } from 'lucide-react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import MessageContentWithButtons from './MessageContentWithButtons';

interface MessageData {
  id?: number;
  role: 0 | 1 | '0' | '1';
  content: string;
  timestamp?: string;
  isComplete?: boolean;
  notification_id?: number;
}

export interface MessageRef {
  playAudio: () => void;
  scrollIntoView: () => void;
}

interface MessageProps {
  message: MessageData;
  isLast?: boolean;
  onButtonClick?: (buttonText: string) => void;
  withBlurShadow?: boolean;
  ref?: React.Ref<MessageRef>;
}

// React 19: ref is now a regular prop, forwardRef is deprecated
const Message: React.FC<MessageProps> = ({
  message,
  isLast: _isLast,
  onButtonClick,
  withBlurShadow = false,
  ref,
}) => {
    const isAssistant = message.role === 1 || message.role === '1';
    const isTip = !!message.notification_id;

    // Parse buttons from message content
    const { displayContent, buttons, buttonPlaceholder } = useMemo(() => {
      if (!message.content || !isAssistant) {
        return {
          displayContent: message.content,
          buttons: [] as string[],
          buttonPlaceholder: null,
        };
      }

      // First, check for complete button patterns in original content
      const buttonRegex = /\[BUTTONS:([^\]]+)\]/;
      const match = message.content.match(buttonRegex);

      let parsedButtons: string[] = [];
      let hasButtons = false;

      if (match?.[1]) {
        parsedButtons = match[1].split('|').map((btn) => btn.trim());
        hasButtons = true;
      }

      // Process content for display
      let processedContent = message.content;

      if (!message.isComplete) {
        // Replace any complete button patterns with placeholder
        if (hasButtons) {
          processedContent = processedContent.replace(buttonRegex, '\u200B');
        }

        // Then check for incomplete button pattern at the end
        const lastOpenBracket = processedContent.lastIndexOf('[');
        if (lastOpenBracket !== -1) {
          const afterBracket = processedContent.substring(lastOpenBracket);
          const upperAfterBracket = afterBracket.toUpperCase();

          // Check if this is a potential incomplete button pattern
          const isPotentialButton =
            afterBracket === '[' || // Just the bracket
            upperAfterBracket.startsWith('[B') || // Could be [BUTTONS
            (upperAfterBracket.match(/^\[BUTTONS:/i) &&
              !afterBracket.includes(']')); // Definite button pattern

          if (isPotentialButton) {
            // Hide only the incomplete button syntax at the end
            processedContent = processedContent.substring(0, lastOpenBracket);
          }
        }
      } else if (hasButtons) {
        // For complete messages, also replace buttons with placeholder
        processedContent = processedContent.replace(buttonRegex, '\u200B');
      }

      return {
        displayContent: processedContent,
        buttons: parsedButtons,
        buttonPlaceholder: hasButtons ? '\u200B' : null,
      };
    }, [message.content, isAssistant, message.isComplete]);

    // Use ref for direct DOM access instead of querySelector
    const messageRef = useRef<HTMLDivElement>(null);

    // Memoize the scrollIntoView callback
    const scrollIntoViewHandler = useCallback(() => {
      if (messageRef.current) {
        messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        playAudio: () => {
          // Audio disabled
        },
        scrollIntoView: scrollIntoViewHandler,
      }),
      [scrollIntoViewHandler]
    );

    return (
      <article
        ref={isTip ? messageRef : undefined}
        className={`flex w-full space-x-3 max-w-2xl mx-auto p-4 ${
          isAssistant ? 'justify-start' : 'justify-end'
        }`}
        role="article"
        aria-label={isAssistant ? 'Wiadomosc od asystenta' : 'Twoja wiadomosc'}
        {...(isTip ? { 'data-tip-id': message.notification_id } : {})}
      >
        {isAssistant && (
          <div className="flex-shrink-0" aria-hidden="true">
            <img
              src="/assets/bot-avatar.png"
              alt=""
              className="w-10 h-[27px] object-contain"
            />
          </div>
        )}

        <div className={`flex-1 ${isAssistant ? 'mr-12' : 'ml-12'}`}>
          <div
            className={`rounded-card p-4 space-y-3 relative ${
              withBlurShadow ? '' : 'shadow-lawmingo'
            } ${
              isAssistant
                ? 'bg-lawmingo-charcoal/90 backdrop-blur-sm text-lawmingo-cream'
                : 'bg-accent-600/90 backdrop-blur-sm text-lawmingo-charcoal'
            }`}
            style={
              withBlurShadow
                ? {
                    backdropFilter: 'blur(4px)',
                  }
                : undefined
            }
          >
            {isTip && (
              <div className="absolute -top-3 right-3">
                <span
                  className="px-3 py-1 text-sm font-semibold rounded-md
                  bg-accent-600 text-lawmingo-charcoal shadow-md"
                >
                  Tip
                </span>
              </div>
            )}

            {isAssistant && !message.content && !message.isComplete && (
              <div className="flex items-center space-x-2 text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-t-transparent border-accent-600"></div>
                <span>Myślę...</span>
              </div>
            )}

            {message.content && (
              <div className="space-y-3">
                <div
                  className={`markdown ${
                    isAssistant ? 'text-lawmingo-cream' : 'text-lawmingo-charcoal'
                  }`}
                  style={
                    withBlurShadow
                      ? {
                          WebkitMaskImage:
                            'linear-gradient(to bottom, black 0%, black 20%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.05) 100%)',
                          maskImage:
                            'linear-gradient(to bottom, black 0%, black 20%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.05) 100%)',
                        }
                      : undefined
                  }
                >
                  <MessageContentWithButtons
                    content={displayContent}
                    isComplete={message.isComplete ?? false}
                    buttons={buttons}
                    buttonPlaceholder={buttonPlaceholder}
                    {...(onButtonClick !== undefined && { onButtonClick })}
                  />
                </div>
              </div>
            )}

            {!withBlurShadow && message.timestamp && (
              <div
                className={`text-xs mt-2 ${
                  isAssistant
                    ? 'text-lawmingo-cream/50'
                    : 'text-lawmingo-charcoal/70'
                }`}
              >
                {format(new Date(message.timestamp), 'd MMMM yyyy • HH:mm', {
                  locale: pl,
                })}
              </div>
            )}
          </div>
        </div>

        {!isAssistant && (
          <div className="flex-shrink-0" aria-hidden="true">
            <User2 className="h-10 w-10 text-accent-600" aria-hidden="true" />
          </div>
        )}
      </article>
    );
  };

export default React.memo(Message);
